/**
 * Loads Site ID autocomplete options for the SSOT Web Form header.
 *
 * When the user pastes multiple values (comma / semicolon / newline-separated) it
 * queries the backend for all of them at once, pre-selects the found codes, and
 * creates manual (`__isNew__`) options for any codes not found in the server.
 *
 * @param {string} val - Current text input value.
 * @param {Object} formik - Formik instance used to set `site_ids` after a bulk paste.
 * @param {Object} ctx - Context dependencies.
 * @param {boolean}  ctx.hasContext - Whether a valid OET and Run context is available.
 * @param {string|null} ctx.oetId - OET ID passed to the autocomplete API (may be null).
 * @param {string|null} ctx.runId - Run ID passed to the autocomplete API (may be null).
 * @param {Function} ctx.setInputValueSite - State setter to clear the text input after bulk paste.
 * @param {Function} ctx.splitText - Utility that splits comma/semicolon/newline strings into arrays.
 * @param {Function} ctx.getOETSitesAutoComplete - Service call: `(oetId, runId, search?, list?) => Promise`.
 * @returns {Promise<Array<{value: string|number, label: string, __isNew__?: boolean}>>}
 */
export const loadSiteIdOptions = (
    val,
    formik,
    {
        hasContext,
        oetId,
        runId,
        setInputValueSite,
        splitText,
        getOETSitesAutoComplete
    }
) => {
    if (!hasContext) {
        return Promise.resolve([]);
    }

    const search_list = val ? splitText(val) : [val];

    if (search_list.length > 1) {
        // Only use the new search list (pasted values)
        // Clean and filter empty values
        let new_search_list = search_list.filter(v => v && v.trim());

        return getOETSitesAutoComplete(oetId, runId, null, new_search_list)
            .then(resp => {
                // Site IDs found in the server
                const site_list = resp.map(site => {
                    return {
                        value: site.id,
                        label: site.customer_site_code,
                    };
                });

                const customer_site_code_list = resp.map(site => site.customer_site_code);
                const notFound = new_search_list.filter(x => customer_site_code_list.indexOf(x) < 0);

                // Create manual options for Site IDs not found in the server
                const manualOptions = notFound.map(code => ({
                    value: code,
                    label: code,
                    __isNew__: true
                }));

                // Combine both found and manual options
                const combinedList = [...site_list, ...manualOptions];

                if (combinedList.length > 0) {
                    setInputValueSite("");
                    formik.setFieldValue("site_ids", combinedList, false);
                }

                return combinedList;
            });
    }

    return getOETSitesAutoComplete(oetId, runId, val).then(resp => {
        return resp.map(v => {
            return { value: v.id, label: v.customer_site_code };
        });
    });
};

/**
 * Formik validation function for the SSOT Web Form header.
 * All four fields are required before an NDPD search can be triggered.
 *
 * @param {Object}    values - Current Formik field values.
 * @param {Object|null} values.activity - Selected activity option `{value, label}`.
 * @param {Array}     values.site_ids - Selected site ID options.
 * @param {Date|null} values.customer_request_delivery_date - Customer delivery date.
 * @param {Date|null} values.project_request_delivery_date - Project delivery date.
 * @returns {Object} Error messages keyed by field name; empty object when all fields are valid.
 */
export const validate = values => {
    const errors = {};
    if (!values.activity) {
        errors.activity = "Activity is required";
    }
    if (!values.site_ids || (Array.isArray(values.site_ids) && values.site_ids.length === 0)) {
        errors.site_ids = "Site ID is required";
    }
    if (!values.customer_request_delivery_date) {
        errors.customer_request_delivery_date = "Customer request delivery date is required";
    }
    if (!values.project_request_delivery_date) {
        errors.project_request_delivery_date = "Project request delivery date is required";
    }
    return errors;
};

/**
 * Transforms Formik form values into the payload shape expected by `ndpdSearch`.
 *
 * - `activity` is extracted as the numeric ID from the select option.
 * - Dates are converted from `Date` objects to ISO 8601 strings.
 * - `customer_site_codes` uses each site option's `label` as the code string.
 *
 * @param {Object} values - Formik field values.
 * @param {{value: number, label: string}} values.activity - Selected activity option.
 * @param {Date|null} values.customer_request_delivery_date
 * @param {Date|null} values.project_request_delivery_date
 * @param {Array<{value: string|number, label: string}>} values.site_ids
 * @returns {{
 *   activity: number,
 *   customer_request_delivery_date: string|null,
 *   project_request_delivery_date: string|null,
 *   customer_site_codes: string[]
 * }}
 */
export function formatSubmitPayload(values) {
  const siteIdsRaw = values.site_ids;
  const siteIdsArray = siteIdsRaw
    ? (Array.isArray(siteIdsRaw) ? siteIdsRaw : [siteIdsRaw])
    : [];
  
  return {
      activity: values.activity.value,
      customer_request_delivery_date: values.customer_request_delivery_date
          ? values.customer_request_delivery_date.toISOString()
          : null,
      project_request_delivery_date: values.project_request_delivery_date
          ? values.project_request_delivery_date.toISOString()
          : null,
      customer_site_codes: siteIdsArray.map(s => s.label),
  };
}